import type { Rect } from './stroke';
import type { TextBox } from '../store/types';
import { resolveInk, type Theme } from '../theme/tokens';

/**
 * UI font stack (matches --font-ui in theme/tokens.css) — shared by canvas
 * rendering and the HTML overlay editor so the two stay WYSIWYG-enough
 * (M3b spec §1.2: small metric drift between canvas and textarea is accepted).
 */
export const FONT_STACK = "'Nunito Sans', ui-rounded, 'Segoe UI', system-ui, sans-serif";

const LINE_HEIGHT = 1.4;

/**
 * Greedy word wrap at width `w`, using an injectable `measure(s)` (pixel
 * width of a string at the box's active font — the caller is responsible for
 * having set that font before measuring, e.g. via ctx.font). Honors explicit
 * `\n` as paragraph breaks and hard-breaks a single word wider than `w`
 * character by character rather than overflowing the box. `fontSize` is
 * accepted (not read here) for signature symmetry with textBoxHeight/callers
 * that also need it — measure already reflects the active font size.
 */
export function wrapText(text: string, w: number, fontSize: number, measure: (s: string) => number): string[] {
  const lines: string[] = [];
  for (const para of text.split('\n')) {
    if (para === '') { lines.push(''); continue; }
    let current = '';
    for (const word of para.split(' ')) {
      if (measure(word) > w) {
        // Over-long word: flush the pending line, then hard-break char by char.
        if (current) { lines.push(current); current = ''; }
        let chunk = '';
        for (const ch of word) {
          const candidate = chunk + ch;
          if (measure(candidate) > w && chunk) { lines.push(chunk); chunk = ch; }
          else chunk = candidate;
        }
        current = chunk; // remainder continues wrapping with whatever word comes next
        continue;
      }
      const candidate = current ? `${current} ${word}` : word;
      if (measure(candidate) > w && current) { lines.push(current); current = word; }
      else current = candidate;
    }
    lines.push(current);
  }
  return lines;
}

/** Total height of wrapped text at 1.4x line-height. */
export function textBoxHeight(lines: string[], fontSize: number): number {
  return lines.length * fontSize * LINE_HEIGHT;
}

/** World-space rect a text box currently occupies (x/y/w fixed on the box; h derived from wrapped text). */
export function textBoxRect(box: TextBox, measure: (s: string) => number): Rect {
  const lines = wrapText(box.text, box.w, box.fontSize, measure);
  return { x: box.x, y: box.y, w: box.w, h: textBoxHeight(lines, box.fontSize) };
}

/**
 * Canvas font string for a box: `[italic ][bold ]{fontSize}px {FONT_STACK}`.
 * Exported so callers that need to measure a box's rect outside of an actual
 * draw (renderer/thumbnail invalidation bookkeeping) can set `ctx.font` the
 * same way drawTextBox does, keeping measurement consistent everywhere.
 */
export function textBoxFont(box: Pick<TextBox, 'bold' | 'italic' | 'fontSize'>): string {
  return `${box.italic ? 'italic ' : ''}${box.bold ? 'bold ' : ''}${box.fontSize}px ${FONT_STACK}`;
}

/**
 * Draws a text box's wrapped lines with fillText, top-anchored at (box.x,
 * box.y). Caller is responsible for skipping deletedAt (tombstoned) boxes,
 * same convention as the stroke tile/thumbnail passes.
 */
export function drawTextBox(ctx: CanvasRenderingContext2D, box: TextBox, theme: Theme): void {
  ctx.save();
  ctx.font = textBoxFont(box);
  const measure = (s: string) => ctx.measureText(s).width;
  const lines = wrapText(box.text, box.w, box.fontSize, measure);
  const lineHeight = box.fontSize * LINE_HEIGHT;
  ctx.fillStyle = resolveInk(box.color, theme);
  ctx.textBaseline = 'top';
  lines.forEach((line, i) => ctx.fillText(line, box.x, box.y + i * lineHeight));
  ctx.restore();
}

/**
 * Topmost box (last in array order) whose rect contains the point, or null.
 * Tombstoned boxes are skipped defensively even though callers are expected
 * to pass only live boxes.
 */
export function hitTextBox(boxes: TextBox[], x: number, y: number, measure: (s: string) => number): TextBox | null {
  for (let i = boxes.length - 1; i >= 0; i--) {
    const box = boxes[i]!;
    if (box.deletedAt) continue;
    const r = textBoxRect(box, measure);
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return box;
  }
  return null;
}
